import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faWarning } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../../components/Modal";
import Button from "../../../../../components/Button";
import { useConfigurationModule } from "../../../Configuration/hooks/useConfigurationModule";

function ConfirmModal({ visible, selected, close, trigger, isMutating }) {
    const { t } = useTranslation();
    const hideRetroAdjustment = useConfigurationModule("timeSheetGeneral.hideRetroAdjustment");

    const { data } = useSWR(() => {
        if (selected?.status !== "locked" || hideRetroAdjustment) {
            return null;
        }

        return {
            url: "/retro-adjustment/count-by-pay-period",
            params: {
                status: ["pending", "approved"],
                startDate: moment(selected?.periodStartDate).format("YYYY-MM-DD"),
                endDate: moment(selected?.periodEndDate).format("YYYY-MM-DD"),
                payGroupItem: selected?.id
            },
        }
    });

    const content = useMemo(() => {
        const locked = selected?.status === "locked";
        const count = data ?? 0;

        if (!locked) {
            return (
                <div className="d-flex align-items-start flex-column gap-3 m-3">
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faLock} size="lg" className="text-primary mt-1" />
                        <div className="font-weight-600">
                            {t("lock-pay-group-item")}
                        </div>
                    </div>
                    <div>
                        <div className="text-muted">
                            {t("lock-description")}
                        </div>

                        <div className="mt-2">
                            {t("lock-confirm")}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="d-flex align-items-start flex-column gap-3 m-3">
                <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faWarning} size="lg" className="text-warning" />
                    <div className="font-weight-bold mb-0 p-0">
                        {t("unlock-pay-group-item")}
                    </div>
                </div>

                <div>
                    <div className="text-muted">
                        {(count > 0 && !hideRetroAdjustment)
                            ? t("unlock-description-with-count", { count })
                            : t("unlock-description")}
                    </div>

                    {(count > 0 && !hideRetroAdjustment) && (
                        <div className="mt-2 text-danger">
                            {t("unlock-warning")}
                        </div>
                    )}

                    <div className="mt-2">
                        {t("unlock-confirm")}
                    </div>
                </div>
            </div>
        );
    }, [selected?.status, data, hideRetroAdjustment, t]);

    return (
        <Modal
            isOpen={visible}
            close={close}
            centered
        >
            {content}
            <div className="d-flex justify-content-end">
                <Button onClick={close}>
                    {t("cancel")}
                </Button>
                <Button color="primary" onClick={() => trigger(selected)}>
                    {t("confirm")}
                </Button>
            </div>
        </Modal>
    );
}

export default ConfirmModal;
